import { useEffect, useState } from 'react';
import { ownerKyc, api } from '../lib/api';
import { toast } from 'sonner';
import { ShieldCheck, Lock, AlertTriangle, Check, Loader2, ExternalLink, BadgeCheck, BadgeX } from 'lucide-react';

const STATUS_LABEL = {
  pending: { label: 'Not started', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  submitted: { label: 'Submitted, pending verification', cls: 'bg-blue-50 text-blue-800 border-blue-200' },
  verified: { label: 'Verified', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rejected', cls: 'bg-red-50 text-red-700 border-red-200' },
};

export default function OwnerKycForm() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    full_name: '', aadhaar: '', pan: '', gstin: '',
    bank_account: '', ifsc: '', bank_holder_name: '', upi_id: '',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    ownerKyc.get().then((d) => {
      setData(d);
      setForm({
        full_name: d.full_name || '',
        aadhaar: '',  // never prefill; server returns masked only
        pan: d.pan || '',
        gstin: d.gstin || '',
        bank_account: '',  // never prefill
        ifsc: d.bank_ifsc || '',
        bank_holder_name: d.bank_holder_name || '',
        upi_id: d.upi_id || '',
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      // Send only fields that have a real value or were edited (skip empty unchanged sensitive)
      const payload = {};
      ['full_name', 'pan', 'gstin', 'ifsc', 'bank_holder_name', 'upi_id'].forEach(k => {
        if (form[k] !== '' || data?.[k] !== undefined) payload[k] = form[k];
      });
      if (form.aadhaar) payload.aadhaar = form.aadhaar;
      if (form.bank_account) payload.bank_account = form.bank_account;
      await ownerKyc.put(payload);
      toast.success('KYC saved. Verification pending.');
      const fresh = await ownerKyc.get();
      setData(fresh);
      setForm(f => ({ ...f, aadhaar: '', bank_account: '' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  if (!data) return <div className="card-soft p-5 text-sm text-ink/60 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading KYC…</div>;

  const status = STATUS_LABEL[data.kyc_status] || STATUS_LABEL.pending;

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] text-[#1B5E20] grid place-items-center shrink-0"><ShieldCheck className="w-6 h-6"/></div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1B5E20]">KYC & Bank details</div>
          <h2 className="font-heading font-black text-2xl">Shop owner verification</h2>
          <p className="text-xs text-ink/60 mt-0.5 max-w-2xl">Required for legal compliance (GST/PAN) and to receive refunds/returns directly to your bank account. <span className="font-semibold">Encrypted in our backend — never shown to customers.</span></p>
        </div>
        <span data-testid="kyc-status-pill" className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${status.cls}`}>{status.label}</span>
      </div>

      <div className="card-soft p-5">
        <div className="font-heading font-bold text-base mb-4 flex items-center gap-2"><Lock className="w-4 h-4 text-[#D35400]"/>Identity & tax</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Full legal name" testId="kyc-fullname"><input value={form.full_name} onChange={e=>setForm({...form, full_name:e.target.value})} className="kinp" placeholder="As per Aadhaar"/></Field>
          <Field label="Aadhaar number" hint={data.aadhaar_set ? `Saved: ${data.aadhaar_masked}` : '12 digits, encrypted at rest'} testId="kyc-aadhaar">
            <input value={form.aadhaar} onChange={e=>setForm({...form, aadhaar:e.target.value.replace(/\D/g,'').slice(0,12)})} className="kinp" placeholder={data.aadhaar_set ? 'Enter new to replace' : 'XXXXXXXXXXXX'} inputMode="numeric"/>
          </Field>
          <Field label="PAN number" testId="kyc-pan">
            <input value={form.pan} onChange={e=>setForm({...form, pan:e.target.value.toUpperCase().slice(0,10)})} className="kinp" placeholder="ABCDE1234F"/>
          </Field>
          <Field label="GSTIN (optional)" hint={
            data.gstin_verified ? <span className="text-[#1B5E20] inline-flex items-center gap-1 font-semibold"><BadgeCheck className="w-3 h-3"/>Verified by GST checksum {data.gstin_verified_at && `· ${new Date(data.gstin_verified_at).toLocaleDateString()}`}</span> :
            (data.gstin ? <span className="text-amber-700 inline-flex items-center gap-1"><BadgeX className="w-3 h-3"/>Checksum mismatch — please re-enter from your GST certificate</span> : 'If you have GST registration')
          } testId="kyc-gstin">
            <input value={form.gstin} onChange={e=>setForm({...form, gstin:e.target.value.toUpperCase().slice(0,15)})} className="kinp" placeholder="27AAPFU0939F1ZV"/>
          </Field>
        </div>

        {/* Official Govt verification deep-links */}
        <div className="mt-4 rounded-lg border border-dashed border-border bg-[#FBF8F1] p-3">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/70 mb-2">Verify on official Govt websites</div>
          <div className="flex flex-wrap gap-2">
            <a data-testid="verify-aadhaar-link" href="https://myaadhaar.uidai.gov.in/check-aadhaar-validity" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-white border border-border hover:border-[#E67E22] hover:text-[#D35400] px-3 py-1.5 text-xs font-semibold">
              <ExternalLink className="w-3 h-3"/>Aadhaar (UIDAI)
            </a>
            <a data-testid="verify-pan-link" href="https://www.incometax.gov.in/iec/foportal/help/individual/return-applicable-1" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-white border border-border hover:border-[#E67E22] hover:text-[#D35400] px-3 py-1.5 text-xs font-semibold">
              <ExternalLink className="w-3 h-3"/>PAN (Income Tax)
            </a>
            <a data-testid="verify-gstin-link" href="https://services.gst.gov.in/services/searchtp" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-white border border-border hover:border-[#E67E22] hover:text-[#D35400] px-3 py-1.5 text-xs font-semibold">
              <ExternalLink className="w-3 h-3"/>GSTIN (GST Portal)
            </a>
            <button data-testid="reverify-gstin-btn" type="button" disabled={!data.gstin} onClick={async () => {
              try {
                const r = await api.post('/owner/kyc/verify-gstin');
                if (r.data.verified) toast.success('GSTIN checksum valid — verified.');
                else toast.error(r.data.message);
                const fresh = await ownerKyc.get();
                setData(fresh);
              } catch (e) { toast.error(e?.response?.data?.detail || 'Verification failed'); }
            }} className="inline-flex items-center gap-1.5 rounded-full bg-[#1B5E20] hover:bg-[#0D3D14] disabled:opacity-40 text-white px-3 py-1.5 text-xs font-semibold">
              <BadgeCheck className="w-3 h-3"/>Re-verify GSTIN
            </button>
          </div>
          <p className="text-[11px] text-ink/50 mt-2">Click a link, complete the verification on the official Govt site, then return here to mark complete. Your GSTIN is auto-verified via the GST Council's checksum algorithm (offline).</p>
        </div>
      </div>

      <div className="card-soft p-5">
        <div className="font-heading font-bold text-base mb-4 flex items-center gap-2"><Lock className="w-4 h-4 text-[#D35400]"/>Bank account (for refunds)</div>
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 mb-3 flex gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>This is where customer refunds/returns will be sent. Double-check before saving.</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Account holder name" testId="kyc-holder"><input value={form.bank_holder_name} onChange={e=>setForm({...form, bank_holder_name:e.target.value})} className="kinp"/></Field>
          <Field label="Bank account number" hint={data.bank_account_set ? `Saved: ${data.bank_account_masked}` : '9-18 digits'} testId="kyc-account">
            <input value={form.bank_account} onChange={e=>setForm({...form, bank_account:e.target.value.replace(/\D/g,'').slice(0,18)})} className="kinp" placeholder={data.bank_account_set ? 'Enter new to replace' : ''} inputMode="numeric"/>
          </Field>
          <Field label="IFSC code" testId="kyc-ifsc">
            <input value={form.ifsc} onChange={e=>setForm({...form, ifsc:e.target.value.toUpperCase().slice(0,11)})} className="kinp" placeholder="HDFC0001234"/>
          </Field>
          <Field label="UPI ID (optional)" testId="kyc-upi">
            <input value={form.upi_id} onChange={e=>setForm({...form, upi_id:e.target.value})} className="kinp" placeholder="name@bank"/>
          </Field>
        </div>
      </div>

      <button data-testid="kyc-save-btn" disabled={busy} onClick={save} className="btn-primary">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin"/>Saving…</> : <><Check className="w-4 h-4"/>Save KYC</>}
      </button>

      <style>{`.kinp{width:100%;border:1px solid hsl(var(--border));background:white;border-radius:0.5rem;padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.kinp:focus{border-color:#E67E22;box-shadow:0 0 0 3px rgba(230,126,34,0.15)}`}</style>
    </div>
  );
}

function Field({ label, hint, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink/70 mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-ink/50 mt-1">{hint}</div>}
    </label>
  );
}
